import { ThemeButton } from './Theme';
import React, { useCallback, useState, useEffect } from 'react';
import { Settings, Eye, Code, Save, RotateCcw, RefreshCw, Check, AlertTriangle, History } from 'lucide-react';
import { tradingApi } from '../services/api';
import type { StrategyConfigHistoryEntry } from '../services/api/strategies.ts';
import StrategyConfigPreview from './StrategyConfigPreview';
import { normalizeStrategyConfigForFormat } from './strategyConfigFormat';

interface StrategyConfigPanelProps {
    strategyName: string;
    onConfigChange?: () => void;
    readOnly?: boolean;
    fullBleed?: boolean;
}

const StrategyConfigPanel: React.FC<StrategyConfigPanelProps> = ({ strategyName, onConfigChange, readOnly = false, fullBleed = false }) => {
    const [configJson, setConfigJson] = useState<string>('');
    const [isEnabled, setIsEnabled] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [isPreview, setIsPreview] = useState<boolean>(readOnly);
    const [showHistory, setShowHistory] = useState<boolean>(false);
    const [jsonError, setJsonError] = useState<string | null>(null);
    const [version, setVersion] = useState<number | null>(null);
    const [updatedAt, setUpdatedAt] = useState<string | null>(null);
    const [history, setHistory] = useState<StrategyConfigHistoryEntry[]>([]);
    const [historyLoading, setHistoryLoading] = useState<boolean>(false);
    const [configSchema, setConfigSchema] = useState<Record<string, unknown> | null>(null);

    const validateJson = useCallback((val: string) => {
        try {
            JSON.parse(val);
            setJsonError(null);
            return true;
        } catch (validationError) {
            setJsonError(validationError instanceof Error ? validationError.message : 'Invalid JSON');
            return false;
        }
    }, []);

    const fetchHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const res = await tradingApi.getStrategyDbConfigHistory(strategyName);
            if (res.success) {
                setHistory(res.data?.history || []);
            }
        } catch (err) {
            console.error("Failed to fetch history:", err);
        } finally {
            setHistoryLoading(false);
        }
    }, [strategyName]);

    const fetchConfig = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await tradingApi.getStrategyDbConfig(strategyName);
            if (response.success && response.data) {
                const config = response.data.config;
                setIsEnabled(config.enabled);
                setVersion(config.version || 1);
                setUpdatedAt(config.updated_at);
                setConfigSchema(config.config_schema || config.config_json || {});
                const jsonStr = JSON.stringify(config.config_json || {}, null, 2);
                setConfigJson(jsonStr);
                validateJson(jsonStr);
            } else {
                throw new Error(response.message || 'Failed to fetch config');
            }
        } catch (err: any) {
            console.warn(err);
            setError('Failed to load configuration. Strategy might not be initialized yet.');
        } finally {
            setLoading(false);
        }
    }, [strategyName, validateJson]);

    useEffect(() => {
        fetchConfig();
    }, [fetchConfig]);

    useEffect(() => {
        if (readOnly) {
            setIsPreview(true);
            setShowHistory(false);
        }
    }, [readOnly]);

    useEffect(() => {
        if (showHistory) {
            fetchHistory();
        }
    }, [showHistory, fetchHistory]);

    const handleTextChange = (val: string) => {
        setConfigJson(val);
        validateJson(val);
    };

    const handleFormat = () => {
        try {
            const parsed = JSON.parse(configJson);
            const formatted = JSON.stringify(
                normalizeStrategyConfigForFormat(parsed, configSchema),
                null,
                2,
            );
            setConfigJson(formatted);
            setJsonError(null);
        } catch {
            setError('Cannot format invalid JSON');
        }
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            // Validate JSON
            let parsedConfig;
            try {
                parsedConfig = JSON.parse(configJson);
            } catch {
                throw new Error('Invalid JSON format');
            }

            const res = await tradingApi.updateStrategyDbConfig(strategyName, {
                is_enabled: isEnabled,
                config_json: parsedConfig
            });

            const message = res?.message || res?.data?.message || 'Configuration saved successfully.';
            setSuccess(message);
            // Refresh Strategy Oversight immediately after hot reload so the
            // displayed values and pending rebalance state match the save.
            window.dispatchEvent(new CustomEvent('trading-data-refresh'));
            if (onConfigChange) onConfigChange();
        } catch (err: any) {
            console.warn(err);
            setError(err.message || 'Failed to save configuration');
        } finally {
            setSaving(false);
        }
    };

    const handleReset = async () => {
        if (!window.confirm(`Are you sure you want to reset ${strategyName} configuration to defaults? This will erase all custom settings.`)) {
            return;
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const res = await tradingApi.resetStrategyDbConfig(strategyName);
            if (res.success) {
                setSuccess(res.message || 'Configuration reset to defaults.');
                await fetchConfig();
                window.dispatchEvent(new CustomEvent('trading-data-refresh'));
                if (onConfigChange) onConfigChange();
            } else {
                throw new Error(res.error || res.message || 'Reset failed');
            }
        } catch (err: any) {
            console.warn(err);
            setError(err.message || 'Failed to reset configuration');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return (
        <div className="flex items-center justify-center p-12 bg-white/5 rounded-2xl border border-white/10">
            <RefreshCw className="w-6 h-6 text-neon animate-spin" />
            <span className="ml-3 text-muted text-sm font-medium tracking-wide italic leading-none">Retrieving telemetry...</span>
        </div>
    );

    const renderPreview = () => {
        try {
            const config = JSON.parse(configJson);
            return <StrategyConfigPreview config={config} />;
        } catch {
            return (
                <div className="flex flex-col items-center justify-center h-80 bg-error-500/5 rounded-xl border border-dashed border-error-500/20">
                    <AlertTriangle className="w-8 h-8 text-error-500 mb-3 opacity-50" />
                    <span className="text-error-500 text-sm font-bold uppercase tracking-widest mb-2">Malformed JSON Buffer</span>
                    <ThemeButton variant="ghost" size="xs"
                        onClick={() => setIsPreview(false)}
                        className="uppercase"
                    >
                        Return to Source Editor
                    </ThemeButton>
                </div>
            );
        }
    };

    return (
        <div className={`panel fade-in ${fullBleed ? 'full-bleed-panel' : ''}`}>
            {/* Header Area */}
            <div className="px-4 py-4 border-b border-white/10 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5 min-w-0">
                    <div className="p-1.5 rounded-lg bg-neon/10 text-neon shadow-[0_0_10px_rgba(92,246,205,0.1)] flex-shrink-0">
                        <Settings className="w-4 h-4" />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-sm font-bold text-white tracking-tight leading-tight truncate">{strategyName} <span className="text-muted font-normal text-xs">Config</span></h2>
                        <p className="text-[9px] text-muted uppercase font-black tracking-[0.15em] mt-0.5 truncate">Parameters & Overrides</p>
                    </div>
                </div>

                {!readOnly && (
                    <div className="flex-shrink-0 flex items-center p-1 bg-white/5 border border-white/10 rounded-lg">
                        <button
                            onClick={() => setIsPreview(false)}
                            title="Edit JSON source"
                            className={`flex items-center justify-center w-7 h-7 rounded-md transition-all duration-200 ${!isPreview
                                ? "bg-neon/10 text-neon shadow-[0_0_10px_rgba(45,212,191,0.1)]"
                                : "text-muted hover:text-white hover:bg-white/5"}`}
                        >
                            <Code className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => {
                                setIsPreview(false);
                                setShowHistory(!showHistory);
                            }}
                            title="View history"
                            className={`flex items-center justify-center w-7 h-7 rounded-md transition-all duration-200 ${showHistory
                                ? "bg-warning-500/10 text-warning-500 shadow-[0_0_10px_rgba(251,191,36,0.1)]"
                                : "text-muted hover:text-white hover:bg-white/5"}`}
                        >
                            <History className="w-3.5 h-3.5" />
                        </button>
                        <button
                            onClick={() => {
                                setShowHistory(false);
                                setIsPreview(true);
                            }}
                            title="Preview config"
                            className={`flex items-center justify-center w-7 h-7 rounded-md transition-all duration-200 ${isPreview && !showHistory
                                ? "bg-neon/10 text-neon shadow-[0_0_10px_rgba(45,212,191,0.1)]"
                                : "text-muted hover:text-white hover:bg-white/5"}`}
                        >
                            <Eye className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>

            {/* Version Info Bar */}
            <div className="px-6 py-2 bg-white/5 border-b border-white/5 flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <span className="text-[10px] font-black uppercase tracking-widest text-white/40">
                        Version <span className="text-neon">{version}</span>
                    </span>
                    {updatedAt && (
                        <span className="text-[10px] font-black uppercase tracking-widest text-white/40">
                            Last Modified <span className="text-white/60">{new Date(updatedAt).toLocaleString()}</span>
                        </span>
                    )}
                </div>
            </div>

            <div className="p-6">
                {!isPreview && !showHistory ? (
                    <div className="space-y-4">
                        <div className="flex justify-between items-end mb-1">
                            <div className="flex items-center space-x-2 px-1">
                                <div className={`w-1.5 h-1.5 rounded-full ${jsonError ? 'bg-error-500 animate-pulse' : 'bg-neon shadow-[0_0_5px_var(--neon)]'}`}></div>
                                <span className={`text-[10px] font-black uppercase tracking-widest ${jsonError ? 'text-error-500' : 'text-neon opacity-80'}`}>
                                    {jsonError ? 'Integrity Check Failed' : 'JSON Standard Compliant'}
                                </span>
                            </div>
                            <ThemeButton variant="secondary" size="xs"
                                onClick={handleFormat}
                                className="uppercase tracking-tighter"
                            >
                                Re-format Source
                            </ThemeButton>
                        </div>
                        <div className="relative group">
                            <textarea
                                value={configJson}
                                onChange={(e) => handleTextChange(e.target.value)}
                                className={`ux-scrollbar w-full h-80 bg-black/40 text-info-500/90 font-mono text-xs p-5 rounded-xl border transition-all duration-300 focus:outline-hidden ${jsonError
                                    ? 'border-error-500/30 focus:border-error-500/50'
                                    : 'border-white/5 focus:border-neon/30 focus:bg-black/60 shadow-inner'}`}
                                spellCheck={false}
                            />
                            <div className="absolute inset-0 rounded-xl pointer-events-none border border-white/5 group-hover:border-white/10 transition-colors"></div>
                        </div>
                        {jsonError && (
                            <div className="text-[10px] text-error-500/80 font-mono bg-error-500/5 p-3 rounded-lg border border-error-500/10 fade-in">
                                <span className="text-error-500 font-bold">ERROR:</span> {jsonError}
                            </div>
                        )}
                    </div>
                ) : showHistory ? (
                    <div className="fade-in h-80 overflow-y-auto ux-scrollbar bg-black/20 rounded-xl border border-white/5 p-4 space-y-3">
                        {historyLoading && history.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-4">
                                <RefreshCw className="w-5 h-5 text-neon animate-spin opacity-50" />
                                <span className="text-[10px] text-muted uppercase font-black tracking-widest">Retrieving Timeline...</span>
                            </div>
                        ) : history.length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full space-y-4 opacity-40">
                                <History className="w-8 h-8 text-muted" />
                                <span className="text-[10px] text-muted uppercase font-black tracking-widest">No previous iterations found</span>
                            </div>
                        ) : (
                            history.map((entry, idx) => (
                                <div key={idx} className="p-4 rounded-xl bg-white/5 border border-white/5 hover:border-white/10 transition-all group">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <span className="text-[10px] font-black text-warning-500 uppercase tracking-widest block mb-1">Iteration {entry.version}</span>
                                            <span className="text-[9px] text-muted font-bold uppercase tracking-tighter italic">
                                                {entry.created_at ? new Date(entry.created_at).toLocaleString() : 'Timestamp unavailable'}
                                            </span>
                                        </div>
                                        <ThemeButton variant="ghost" size="xs"
                                            onClick={() => {
                                                setConfigJson(JSON.stringify(entry.config_json, null, 2));
                                                setShowHistory(false);
                                                setSuccess(`Version ${entry.version} restored to editor. Commit to apply.`);
                                            }}
                                            className="uppercase tracking-widest opacity-0 group-hover:opacity-100"
                                        >
                                            Restore
                                        </ThemeButton>
                                    </div>
                                    <div className="h-24 overflow-hidden relative rounded-lg border border-white/5 bg-black/20 p-2 cursor-pointer" onClick={() => {
                                        setConfigJson(JSON.stringify(entry.config_json, null, 2));
                                        setShowHistory(false);
                                    }}>
                                        <pre className="text-[9px] font-mono text-white/30 truncate">
                                            {JSON.stringify(entry.config_json, null, 2)}
                                        </pre>
                                        <div className="absolute inset-0 bg-linear-to-t from-black/60 to-transparent"></div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                ) : (
                    <div className="fade-in h-80 overflow-hidden">
                        {renderPreview()}
                    </div>
                )}
            </div>

            {/* Notifications Section */}
            {(error || success) && (
                <div className="px-6 pb-2">
                    {error && (
                        <div className="mb-4 text-error-500 text-xs font-bold bg-error-500/10 p-4 rounded-xl border border-error-500/20 flex items-center gap-3 fade-in">
                            <AlertTriangle className="w-4 h-4" />
                            <span>{error}</span>
                        </div>
                    )}
                    {success && (
                        <div className="mb-4 text-neon text-xs font-bold bg-neon/5 p-4 rounded-xl border border-neon/20 flex items-center gap-3 fade-in">
                            <Check className="w-4 h-4" />
                            <span>{success}</span>
                        </div>
                    )}
                </div>
            )}

            {/* Footer Control Bar */}
            <div className="px-6 py-5 bg-white/2 border-t border-white/10 flex flex-wrap justify-between items-center gap-4">
                {readOnly ? (
                    <ThemeButton variant="ghost" size="sm"
                        onClick={fetchConfig}
                        className="flex items-center gap-2 uppercase tracking-widest"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Sync
                    </ThemeButton>
                ) : (
                <>
                <ThemeButton variant="danger" size="sm"
                    onClick={handleReset}
                    className="flex items-center gap-2 uppercase tracking-widest"
                    disabled={saving}
                >
                    <RotateCcw className="w-3.5 h-3.5" />
                    Factory Reset
                </ThemeButton>

                <div className="flex items-center gap-3">
                    <ThemeButton variant="ghost" size="sm"
                        onClick={fetchConfig}
                        className="flex items-center gap-2 uppercase tracking-widest"
                        disabled={saving}
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
                        Sync
                    </ThemeButton>
                    <button
                        onClick={handleSave}
                        disabled={saving || !!jsonError}
                        className={`flex items-center gap-2 px-8 py-2.5 text-black text-xs font-black uppercase tracking-widest rounded-xl transition-all duration-500 shadow-[0_0_20px_rgba(92,246,205,0.1)] active:scale-95 ${saving || !!jsonError
                            ? 'bg-white/10 text-white/30 cursor-not-allowed border border-white/10'
                            : 'bg-neon hover:shadow-[0_0_30px_rgba(42,212,191,0.3)] hover:brightness-110'}`}
                    >
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin text-black" /> : <Save className="w-4 h-4" />}
                        {saving ? 'Transmitting' : 'Commit Config'}
                    </button>
                </div>
                </>
                )}
            </div>
        </div>
    );
};

export default StrategyConfigPanel;
